import React, { useEffect, useState } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/LabReportsEntry.css";
import "../../../center admin css/centerAdmin.css";

interface CariogramActivePatientCardProps {
  patient?: {
    id?: string;
    emrId?: string;
    name?: string;
    genderShort?: "M" | "F";
    ageYears?: number;
    avatarUrl?: string;
    sessionId?: string;
    queueStatus?: "WAITING" | "IN_PROGRESS";
  };
}

const CariogramActivePatientCard: React.FC<
  CariogramActivePatientCardProps
> = ({ patient }) => {
  const [displayUrl, setDisplayUrl] = useState<string | undefined>();

  useEffect(() => {
    const fetchSignedUrl = async () => {
      setDisplayUrl(undefined);

      // No active patient = no card image
      if (!patient) {
        return;
      }

      const profileKey = patient.avatarUrl || "";

      if (!profileKey) {
        return;
      }

      try {
        const token = sessionStorage.getItem("accessToken");

        if (!token) {
          console.warn(
            "CariogramActivePatientCard: access token not found",
          );
          return;
        }

        // Already a signed S3 URL
        if (
          profileKey.includes("X-Amz-Signature") ||
          profileKey.includes("X-Amz-Credential")
        ) {
          setDisplayUrl(profileKey);
          return;
        }

        // Remove leading slash if present
        const cleanPath = profileKey.startsWith("/")
          ? profileKey.slice(1)
          : profileKey;

        const parts = cleanPath.split("/");

        // Expected:
        // checkin/capture-photo/P-2026-xxxx/filename.jpg
        //
        // module = checkin/capture-photo
        // key    = P-2026-xxxx/filename.jpg
        if (parts.length < 3) {
          console.warn(
            "CariogramActivePatientCard: invalid profile key:",
            profileKey,
          );
          return;
        }

        const module = `${parts[0]}/${parts[1]}`;
        const key = parts.slice(2).join("/");

        const response = await api.S3config.get("/view-url", {
          params: {
            module,
            key,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const signedUrl =
          response.data?.url || (response as any).url;

        if (signedUrl) {
          setDisplayUrl(signedUrl);
        }
      } catch (err) {
        console.error(
          "CariogramActivePatientCard: failed to fetch presigned URL:",
          err,
        );
      }
    };

    fetchSignedUrl();
  }, [patient?.avatarUrl, patient?.emrId]);

  const genderText =
    patient?.genderShort === "M"
      ? "Male"
      : patient?.genderShort === "F"
        ? "Female"
        : "-";

  const ageText =
    typeof patient?.ageYears === "number"
      ? `${patient.ageYears} Years`
      : "-";

  // Important: do not render anything when there is no selected patient.
  if (!patient) {
    return null;
  }

  return (
    <div className="patient-details-card">
      <div className="patient-details-top">
        <div className="patient-avatar-large">
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="Patient"
              className="patient-avatar-img"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="patient-avatar-fallback" />
          )}
        </div>

        <div className="patient-info-group">
          <div className="patient-info-row">
            <div>
              <span className="field-label">EMR ID</span>
              <div className="field-value">
                {patient.emrId ?? "-"}
              </div>
            </div>

            <div>
              <span className="field-label">Name</span>
              <div className="field-value">
                {patient.name ?? "-"}
              </div>
            </div>
          </div>

          <div className="patient-info-row">
            <div>
              <span className="field-label">Age</span>
              <div className="field-value">{ageText}</div>
            </div>

            <div>
              <span className="field-label">Gender</span>
              <div className="field-value">{genderText}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CariogramActivePatientCard;